import type { SubmitFormError } from "@features/errors/types";
import type { FieldValues, Path, UseFormSetError } from "react-hook-form";

import { useCallback, useRef } from "react";

import { useHookOption } from "@features/errors/hooks/use-hook-option.ts";
import { makeSubmitFormError } from "@features/errors/libs/factory";
import {
  isErrorCode,
  isQueryApiError,
  isSubmitFormError,
} from "@features/errors/libs/guards";
import { type CodeExpr, type ErrorCode } from "@kex-resources/api-errors";

export const useFormSubmitErrors = <T, E extends ErrorCode>(
  onSubmit: (data: T) => Promise<void>,
  errorCodes: E[],
  handlers: Record<
    E,
    (error: SubmitFormError<CodeExpr, E, Extract<keyof T, string>>) => void
  >,
) => {
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;
  const errorsRef = useRef(errorCodes);
  errorsRef.current = errorCodes;

  return useCallback(
    async (data: T) => {
      try {
        await onSubmit(data);
      } catch (error) {
        const currentErrors = errorsRef.current;
        if (
          !isSubmitFormError<E, Extract<keyof T, string>>(error, currentErrors)
        ) {
          return;
        }

        const currentHandlers = handlersRef.current;
        const errorHandler = currentHandlers[error.code];
        return errorHandler(error);
      }
    },
    [onSubmit],
  );
};

export const useSetServerError = <T extends FieldValues>(
  setError: UseFormSetError<T>,
) => {
  const setFieldError = <C extends ErrorCode, F extends Path<T>>(
    error: SubmitFormError<CodeExpr, C, F>,
  ) => {
    for (const fieldName of error.fieldNames) {
      const message = error.fieldErrorMessage?.[fieldName];

      setError(fieldName, {
        message,
        type: "server",
      });
    }
  };

  return { setFieldError };
};

export const useFormSubmitError = <A extends FieldValues, R = void>(
  handler: (formValues: A) => Promise<R>,
  errorMap?: {
    [E in ErrorCode]?: {
      [F in "root" | keyof A]?: (
        serverPayload?: Record<string, unknown>,
      ) => string;
    };
  },
) => {
  const errorMapOption = useHookOption(errorMap);
  return useCallback(
    async (formValues: A): Promise<R> => {
      try {
        return await handler(formValues);
      } catch (error) {
        if (!isQueryApiError(error)) {
          throw error;
        }
        const { code, group, payload } = error;

        if (!isErrorCode(code)) {
          throw error;
        }
        const currentErrorMap = errorMapOption.current;
        const errorConfig = currentErrorMap?.[code];

        const fieldNames = Object.keys(errorConfig || {}) as Array<
          "root" | Extract<keyof A, string>
        >;
        const errorMessages: { [L in (typeof fieldNames)[number]]?: string } =
          {};
        for (const fieldName of fieldNames) {
          const errorMessageFormatter = errorConfig?.[fieldName];
          if (errorMessageFormatter !== undefined) {
            errorMessages[fieldName] = errorMessageFormatter(payload);
          }
        }

        throw makeSubmitFormError(
          group,
          code,
          fieldNames,
          errorMessages,
          payload,
        );
      }
    },
    [errorMapOption, handler],
  );
};
